import React, { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import type { DiscLetter } from '../../constants/disc';
import { readQuizDraft, useQuizDraft } from '../CoverQuiz/quizDraft';
import { DISC_ARCHETYPES } from './careerDiscArchetypes';
import {
  ATTRACT_OPTIONS,
  CIRCLE_OPTIONS,
  CONTACT_OPTIONS,
  EMPTY_PART_TWO,
  FAMILY_OPTIONS,
  HEADLINES,
  MILESTONE_OPTIONS,
  PART_TWO_LIMITS,
  PART_TWO_STEPS,
  QUESTION_OPTIONS,
  REPLY_OPTIONS,
  SUPPORT_OPTIONS,
  TEAM_OPTIONS,
  partTwoAdviserNotes,
  partTwoLabels,
  partTwoRoadmap,
  partTwoStepReady,
  teamOrderFor
} from './careerPartTwoData';
import type { Option, PartTwoAnswers, StoredPartTwo } from './careerPartTwoData';
import { L, listJoin, useQuizLang } from '../CoverQuiz/quizLang';
import type { Txt } from '../CoverQuiz/quizLang';
import './CareerQuiz.scss';
import './CareerQuizPartTwo.scss';

/**
 * 職涯測驗 第二部分 — the candidate's side. Seven questions, then 「我的理想事業藍圖」
 * built only from their answers. Wording follows the DISC letter from part 1;
 * with none (no part 1 to read), the candidate picks the type closest to them first.
 *
 * Two uses:
 *  - a preview (no `submission`): nothing is sent; the last button shows the
 *    blueprint. `showAdviserView` adds the box the adviser will see (never on a
 *    candidate's link);
 *  - a `?t=` invitation (`submission`): the last button sends the answers, with
 *    the consent note above it and 重試 on a failure; the blueprint shows once
 *    they are saved, with no way back to change them.
 */
export interface PartTwoSubmission {
  /** 「陳顧問」 or 「你的顧問」, for the consent note. */
  adviserLabel: string;
  onSubmit: (answers: StoredPartTwo) => void;
  sending: boolean;
  failed: boolean;
  submitted: boolean;
}

export interface CareerQuizPartTwoProps {
  /** The part-1 letter; null asks the candidate to pick the closest type first. */
  disc: DiscLetter | null;
  candidateName?: string;
  showAdviserView?: boolean;
  submission?: PartTwoSubmission;
  /** The page's 繁 · 简 · EN switch, top right. */
  langBar?: React.ReactNode;
  /**
   * A real invitation's draft key (quizDraft quizDraftKey): answers in
   * progress survive coming back to the link. The page clears it once sent.
   */
  draftKey?: string | null;
}

/** What coming back to the link brings back (quizDraft useQuizDraft). */
type PartTwoDraft = {
  step: number;
  /** The page's language; CareerQuiz reads it back from this same draft. */
  lang: string;
  answers: PartTwoAnswers;
  pickedDisc: DiscLetter | null;
  discConfirmed: boolean;
};

const STEP_TITLES: Txt[] = [
  L('團隊', '团队', 'Team'),
  L('吸引你的', '吸引你的', 'What draws you'),
  L('朋友圈', '朋友圈', 'Your circle'),
  L('溝通', '沟通', 'Communication'),
  L('家人', '家人', 'Family'),
  L('支援', '支援', 'Support'),
  L('里程碑', '里程碑', 'Milestones'),
  L('問顧問', '问顾问', 'Ask your adviser')
];

const COPY = {
  next: L('下一題', '下一题', 'Next'),
  back: L('上一步', '上一步', 'Back'),
  consent: (button: string, adviser: string) =>
    L(
      `按「${button}」即表示你同意把答案分享給${adviser}，方便下次見面準備合適的資料。`,
      `按「${button}」即表示你同意把答案分享给${adviser}，方便下次见面准备合适的资料。`,
      `By tapping "${button}" you agree to share your answers with ${adviser}, so they can prepare for your next meeting.`
    ),
  sendFailed: L(
    '未能送出，請檢查網絡後再試',
    '未能送出，请检查网络后再试',
    "Couldn't send. Please check your connection and try again."
  ),
  retry: L('重試', '重试', 'Try again'),
  greet: (name: string) => (name ? L(`${name}，`, `${name}，`, `${name}, `) : L('', '', '')),
  teamTitle: L(
    '你最想和什麼樣的人一起打拼？',
    '你最想和什么样的人一起打拼？',
    'Who would you most like to work alongside?'
  ),
  likeYou: L('最像你', '最像你', 'Most like you'),
  attractTitle: L('這份事業，哪一點最吸引你？', '这份事业，哪一点最吸引你？', 'What draws you most to this career?'),
  upTo: (n: number) => L(`最多選 ${n} 項`, `最多选 ${n} 项`, `Pick up to ${n}`),
  circleTitle: L('你身邊最多哪類朋友？', '你身边最多哪类朋友？', 'What kind of friends do you have most?'),
  pickSeveral: L('可以多選', '可以多选', 'Pick as many as apply'),
  contactsTitle: L(
    '平時有聯絡的朋友大約有多少？',
    '平时有联络的朋友大约有多少？',
    'Roughly how many friends do you keep in touch with?'
  ),
  replyTitle: L(
    '朋友說「暫時不需要」，你會怎麼做？',
    '朋友说「暂时不需要」，你会怎么做？',
    'A friend says "not right now". What do you do?'
  ),
  replyDesc: L(
    '沒有標準答案，選最像你的做法',
    '没有标准答案，选最像你的做法',
    "There's no right answer; pick what's most like you"
  ),
  familyTitle: L(
    '如果你決定入行，家人會怎麼看？',
    '如果你决定入行，家人会怎么看？',
    'If you decided to join the industry, how would your family see it?'
  ),
  supportTitle: L(
    '踏出這一步，你最想得到什麼支援？',
    '踏出这一步，你最想得到什么支援？',
    'Taking this step, what support would you most like?'
  ),
  supportDesc: (n: number) =>
    L(
      `最多選 ${n} 項，你的顧問會為你準備`,
      `最多选 ${n} 项，你的顾问会为你准备`,
      `Pick up to ${n}; your adviser will prepare them for you`
    ),
  milestoneTitle: L(
    '你想在這份事業達成什麼里程碑？',
    '你想在这份事业达成什么里程碑？',
    'What milestones would you like to reach in this career?'
  ),
  questionTitle: L('你最想問顧問什麼問題？', '你最想问顾问什么问题？', 'What would you most like to ask your adviser?'),
  questionDesc: (n: number) =>
    L(
      `最多選 ${n} 個，你的顧問下次見面會解答`,
      `最多选 ${n} 个，你的顾问下次见面会解答`,
      `Pick up to ${n}; your adviser will answer them next time you meet`
    ),
  sendAndSee: L('送出，看我的事業藍圖', '送出，看我的事业蓝图', 'Send and see my career blueprint'),
  see: L('看我的事業藍圖 ✨', '看我的事业蓝图 ✨', 'See my career blueprint ✨'),
  discTitle: L(
    '開始之前：你覺得自己比較像哪一種？',
    '开始之前：你觉得自己比较像哪一种？',
    'Before we start: which type feels most like you?'
  ),
  discDesc: L(
    '憑直覺選一個，之後的題目會按你的風格來問',
    '凭直觉选一个，之后的题目会按你的风格来问',
    'Pick one on instinct; the questions will follow your style'
  ),
  start: L('開始', '开始', 'Start'),
  blueprint: L('我的理想事業藍圖', '我的理想事业蓝图', 'My ideal career blueprint'),
  milestonesHeading: L('🏆 你想達成的里程碑', '🏆 你想达成的里程碑', '🏆 Milestones you want to reach'),
  teamHeading: L('🤝 最適合你的團隊', '🤝 最适合你的团队', '🤝 The team that suits you best'),
  teamLine: (team: string, sub: string) => L(`${team}：${sub}。`, `${team}：${sub}。`, `${team}: ${sub}.`),
  startHeading: L('💪 你已經有的起步條件', '💪 你已经有的起步条件', '💪 What you already have to start with'),
  circlesLine: (count: number, circles: string) =>
    L(
      `你身邊有 ${count} 類朋友圈（${circles}），他們就是你第一批可以幫助的人。`,
      `你身边有 ${count} 类朋友圈（${circles}），他们就是你第一批可以帮助的人。`,
      `You have ${count} kinds of friend circles (${circles}): they are the first people you can help.`
    ),
  replyLine: (reply: string) =>
    L(
      `面對「暫時不需要」，你會${reply}，這種做法很適合長線經營。`,
      `面对「暂时不需要」，你会${reply}，这种做法很适合长线经营。`,
      `Faced with "not right now", you would: ${reply}. That works well for building over the long run.`
    ),
  roadmapHeading: L('🗺️ 你的 90 日起步路線圖', '🗺️ 你的 90 日起步路线图', '🗺️ Your 90-day starting roadmap'),
  supportHeading: L(
    '🤝 你想得到的支援，顧問會為你準備',
    '🤝 你想得到的支援，顾问会为你准备',
    '🤝 The support you want, prepared by your adviser'
  ),
  questionsHeading: L(
    '📨 下次見面，你的顧問會解答',
    '📨 下次见面，你的顾问会解答',
    '📨 Your adviser will answer these next time'
  ),
  incomeAsk: L(
    '💰 想知道入行後收入大約有多少？',
    '💰 想知道入行后收入大约有多少？',
    '💰 Curious what you might earn after joining?'
  ),
  incomeSim: L(
    '下次見面，和顧問一起看「收入模擬」。',
    '下次见面，和顾问一起看「收入模拟」。',
    'Go through an income simulation with your adviser next time.'
  ),
  adviserBadge: L(
    '顧問端會看到（候選人看不到）',
    '顾问端会看到（候选人看不到）',
    'What the adviser sees (hidden from the candidate)'
  ),
  radarHeading: L(
    '📡 雷達參考：候選人的答案',
    '📡 雷达参考：候选人的答案',
    "📡 Radar reference: the candidate's answers"
  ),
  radarNote: L(
    '按這些答案，由顧問自己評分。「自律」沒有題目。',
    '按这些答案，由顾问自己评分。「自律」没有题目。',
    'The adviser scores from these answers. "Self-discipline" has no question.'
  ),
  topicsHeading: L('💡 下次見面話題', '💡 下次见面话题', '💡 Topics for next time'),
  sent: (adviser: string) =>
    L(
      `已發送給${adviser}，下次見面一起看！`,
      `已发送给${adviser}，下次见面一起看！`,
      `Sent to ${adviser}. You'll go through it together next time!`
    )
};
const DISC_ORDER: DiscLetter[] = ['D', 'I', 'S', 'C'];

const CareerQuizPartTwo = (props: CareerQuizPartTwoProps) => {
  const { candidateName, showAdviserView, submission } = props;
  const draftKey = submission ? props.draftKey || null : null;
  const lang = useQuizLang();
  const t = (text: Txt) => text[lang];
  const [draft] = useState(() => readQuizDraft<PartTwoDraft>(draftKey));
  const [step, setStep] = useState(draft?.step ?? 0);
  const [answers, setAnswers] = useState<PartTwoAnswers>(
    draft?.answers ? { ...EMPTY_PART_TWO, ...draft.answers } : EMPTY_PART_TWO
  );
  // Only when there is no part-1 letter: the candidate's own pick, then fixed.
  const [pickedDisc, setPickedDisc] = useState<DiscLetter | null>(draft?.pickedDisc ?? null);
  const [discConfirmed, setDiscConfirmed] = useState(draft?.discConfirmed ?? false);
  // Cleared by the page (CareerQuiz) once sent, under the same key.
  useQuizDraft(
    draftKey,
    { step, lang, answers, pickedDisc, discConfirmed },
    { save: Boolean(submission && !submission.submitted), clear: false }
  );
  const disc = props.disc || (discConfirmed ? pickedDisc : null);
  const name = (candidateName || '').trim();
  const done = submission ? submission.submitted : step >= PART_TWO_STEPS;
  const isLast = step === PART_TWO_STEPS - 1;

  const set = (patch: Partial<PartTwoAnswers>) => setAnswers(prev => ({ ...prev, ...patch }));
  const toggle = (field: 'attract' | 'circles' | 'concerns' | 'milestones' | 'questions', key: string, max: number) =>
    setAnswers(prev => {
      const list = prev[field];
      if (list.includes(key)) return { ...prev, [field]: list.filter(k => k !== key) };
      return list.length < max ? { ...prev, [field]: [...list, key] } : prev;
    });

  const scrollTop = () => {
    if (typeof window !== 'undefined' && window.scrollTo) window.scrollTo(0, 0);
  };
  const next = () => {
    if (isLast && submission) {
      submission.onSubmit({ ...answers, disc });
      return;
    }
    setStep(step + 1);
    scrollTop();
  };
  const back = () => setStep(Math.max(0, step - 1));

  const optionList = <K extends string | number>(
    options: Option<K>[],
    isSelected: (key: K) => boolean,
    onPick: (key: K) => void,
    testId: string,
    extra?: (key: K) => React.ReactNode
  ) => (
    <div className="cq-options-list">
      {options.map(option => {
        const selected = isSelected(option.key);
        return (
          <button
            type="button"
            key={String(option.key)}
            className={`cq-option-card cq2-option ${selected ? 'is-selected' : ''}`}
            aria-pressed={selected}
            data-testid={testId}
            data-key={String(option.key)}
            onClick={() => onPick(option.key)}
          >
            <span className="cq-opt-icon" aria-hidden="true">
              {option.icon}
            </span>
            <span className="cq-opt-label cq2-opt-text">
              <b>
                {t(option.label)}
                {extra && extra(option.key)}
              </b>
              {option.sub && <small>{t(option.sub)}</small>}
            </span>
            <span className={`cq-checkbox ${selected ? 'is-checked' : ''}`}>{selected && <Check size={14} />}</span>
          </button>
        );
      })}
    </div>
  );

  const sending = Boolean(submission && submission.sending);
  const actions = (labelTxt: Txt = COPY.next) => {
    const label = t(labelTxt);
    return (
      <>
        {isLast && submission && (
          <p className="cq-consent-note" data-testid="career-quiz2-consent-note">
            {t(COPY.consent(label, submission.adviserLabel))}
          </p>
        )}
        <div className="cq-actions">
          {step > 0 && (
            <button type="button" className="cq-btn-secondary" disabled={sending} onClick={back}>
              <ChevronLeft size={18} /> {t(COPY.back)}
            </button>
          )}
          <button
            type="button"
            className="cq-btn-primary"
            data-testid="career-quiz2-next"
            disabled={!partTwoStepReady(step, answers) || sending}
            onClick={next}
          >
            {label} {isLast && submission ? <Send size={16} /> : <ChevronRight size={18} />}
          </button>
        </div>
        {/* Same as publicQuizLink's RetryBanner — not imported, so this module stays free of the API chain. */}
        {isLast && submission && submission.failed && (
          <div className="cq-retry-banner" data-testid="career-quiz2-retry">
            <span>{t(COPY.sendFailed)}</span>
            <button type="button" className="cq-btn-secondary" disabled={sending} onClick={next}>
              {t(COPY.retry)}
            </button>
          </div>
        )}
      </>
    );
  };

  const question = (letter: DiscLetter) => {
    switch (step) {
      case 0:
        return (
          <>
            <h2 className="cq-question">
              {t(COPY.greet(name))}
              {t(COPY.teamTitle)}
            </h2>
            {optionList(
              teamOrderFor(letter).map(team => TEAM_OPTIONS[team]),
              key => answers.team === key,
              key => set({ team: key }),
              'career-quiz2-team',
              key => (key === letter ? <span className="cq2-tag">{t(COPY.likeYou)}</span> : null)
            )}
            {actions()}
          </>
        );
      case 1:
        return (
          <>
            <h2 className="cq-question">{t(COPY.attractTitle)}</h2>
            <p className="cq-desc">{t(COPY.upTo(PART_TWO_LIMITS.attract))}</p>
            {optionList(
              ATTRACT_OPTIONS,
              key => answers.attract.includes(key),
              key => toggle('attract', key, PART_TWO_LIMITS.attract),
              'career-quiz2-attract'
            )}
            {actions()}
          </>
        );
      case 2:
        return (
          <>
            <h2 className="cq-question">{t(COPY.circleTitle)}</h2>
            <p className="cq-desc">{t(COPY.pickSeveral)}</p>
            {optionList(
              CIRCLE_OPTIONS,
              key => answers.circles.includes(key),
              key => toggle('circles', key, CIRCLE_OPTIONS.length),
              'career-quiz2-circle'
            )}
            <p className="cq2-subq">{t(COPY.contactsTitle)}</p>
            {optionList(
              CONTACT_OPTIONS,
              key => answers.contacts === key,
              key => set({ contacts: key }),
              'career-quiz2-contacts'
            )}
            {actions()}
          </>
        );
      case 3:
        return (
          <>
            <h2 className="cq-question">{t(COPY.replyTitle)}</h2>
            <p className="cq-desc">{t(COPY.replyDesc)}</p>
            {optionList(
              REPLY_OPTIONS,
              key => answers.reply === key,
              key => set({ reply: key }),
              'career-quiz2-reply'
            )}
            {actions()}
          </>
        );
      case 4:
        return (
          <>
            <h2 className="cq-question">{t(COPY.familyTitle)}</h2>
            {optionList(
              FAMILY_OPTIONS,
              key => answers.family === key,
              key => set({ family: key }),
              'career-quiz2-family'
            )}
            {actions()}
          </>
        );
      case 5:
        return (
          <>
            <h2 className="cq-question">{t(COPY.supportTitle)}</h2>
            <p className="cq-desc">{t(COPY.supportDesc(PART_TWO_LIMITS.concerns))}</p>
            {optionList(
              SUPPORT_OPTIONS,
              key => answers.concerns.includes(key),
              key => toggle('concerns', key, PART_TWO_LIMITS.concerns),
              'career-quiz2-support'
            )}
            {actions()}
          </>
        );
      case 6:
        return (
          <>
            <h2 className="cq-question">{t(COPY.milestoneTitle)}</h2>
            <p className="cq-desc">{t(COPY.upTo(PART_TWO_LIMITS.milestones))}</p>
            {optionList(
              MILESTONE_OPTIONS,
              key => answers.milestones.includes(key),
              key => toggle('milestones', key, PART_TWO_LIMITS.milestones),
              'career-quiz2-milestone'
            )}
            {actions()}
          </>
        );
      default:
        return (
          <>
            <h2 className="cq-question">{t(COPY.questionTitle)}</h2>
            <p className="cq-desc">{t(COPY.questionDesc(PART_TWO_LIMITS.questions))}</p>
            {optionList(
              QUESTION_OPTIONS,
              key => answers.questions.includes(key),
              key => toggle('questions', key, PART_TWO_LIMITS.questions),
              'career-quiz2-question'
            )}
            {actions(submission ? COPY.sendAndSee : COPY.see)}
          </>
        );
    }
  };

  const discPicker = () => (
    <div className="cq-card" data-testid="career-quiz2-disc">
      <h2 className="cq-question">
        {t(COPY.greet(name))}
        {t(COPY.discTitle)}
      </h2>
      <p className="cq-desc">{t(COPY.discDesc)}</p>
      {optionList(
        DISC_ORDER.map(letter => ({
          key: letter,
          icon: DISC_ARCHETYPES[letter].emoji,
          label: DISC_ARCHETYPES[letter].name,
          sub: DISC_ARCHETYPES[letter].tagline
        })),
        key => pickedDisc === key,
        key => setPickedDisc(key),
        'career-quiz2-disc-option'
      )}
      <div className="cq-actions">
        <button
          type="button"
          className="cq-btn-primary"
          data-testid="career-quiz2-disc-next"
          disabled={!pickedDisc}
          onClick={() => {
            setDiscConfirmed(true);
            scrollTop();
          }}
        >
          {t(COPY.start)} <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );

  const blueprint = (letter: DiscLetter) => {
    const archetype = DISC_ARCHETYPES[letter];
    const l = partTwoLabels(answers);
    const notes = partTwoAdviserNotes(answers, lang);
    return (
      <div className="cq2-result" data-testid="career-quiz2-result">
        <div className="cq2-hero" style={{ background: `linear-gradient(135deg, ${archetype.color}, #3966f8)` }}>
          <small>
            {archetype.emoji} {t(archetype.name)} · {t(COPY.blueprint)}
          </small>
          <h2 data-testid="career-quiz2-headline">
            {t(COPY.greet(name))}
            {t(HEADLINES[letter])}
          </h2>
          <div className="cq2-hero-tags">
            {l.attract.map(a => (
              <span key={a.key}>
                {a.icon} {t(a.label)}
              </span>
            ))}
          </div>
        </div>

        <section className="cq-card cq2-block">
          <h3>{t(COPY.milestonesHeading)}</h3>
          <div className="cq2-pills">
            {l.milestones.map(m => (
              <span key={m.key}>
                {m.icon} {t(m.label)}
              </span>
            ))}
          </div>
        </section>

        {l.team && (
          <section className="cq-card cq2-block">
            <h3>{t(COPY.teamHeading)}</h3>
            <p>{t(COPY.teamLine(t(l.team.label), l.team.sub ? t(l.team.sub) : ''))}</p>
          </section>
        )}

        <section className="cq-card cq2-block">
          <h3>{t(COPY.startHeading)}</h3>
          <p>
            {t(
              COPY.circlesLine(
                l.circles.length,
                listJoin(
                  l.circles.map(c => t(c.label)),
                  lang
                )
              )
            )}
            {l.reply && t(COPY.replyLine(t(l.reply.label)))}
          </p>
        </section>

        <section className="cq-card cq2-block">
          <h3>{t(COPY.roadmapHeading)}</h3>
          <ul className="cq2-road">
            {partTwoRoadmap(answers, lang).map(item => (
              <li key={item.when}>
                <span>{item.when}</span>
                <div>{item.what}</div>
              </li>
            ))}
          </ul>
        </section>

        {l.concerns.length > 0 && (
          <section className="cq-card cq2-block" data-testid="career-quiz2-support-summary">
            <h3>{t(COPY.supportHeading)}</h3>
            <div className="cq2-pills">
              {l.concerns.map(c => (
                <span key={c.key}>
                  {c.icon} {t(c.label)}
                </span>
              ))}
            </div>
          </section>
        )}

        <section className="cq-card cq2-block">
          <h3>{t(COPY.questionsHeading)}</h3>
          <div className="cq2-pills">
            {l.questions.map(q => (
              <span key={q.key}>
                {q.icon} {t(q.label)}
              </span>
            ))}
          </div>
        </section>

        <div className="cq2-cta">
          <b>{t(COPY.incomeAsk)}</b>
          <span>{t(COPY.incomeSim)}</span>
        </div>

        {showAdviserView && (
          <section className="cq-card cq2-block cq2-adviser" data-testid="career-quiz2-adviser">
            <span className="cq2-badge">{t(COPY.adviserBadge)}</span>
            <h3>{t(COPY.radarHeading)}</h3>
            <div className="cq2-radar">
              {notes.rows.map(row => (
                <div key={row.axis}>
                  <span>{row.axis}</span>
                  <span>{row.text}</span>
                </div>
              ))}
            </div>
            <p className="cq2-note">{t(COPY.radarNote)}</p>
            {notes.flags.map(flag => (
              <div key={flag} className="cq2-flag">
                ⚠️ {flag}
              </div>
            ))}
            <h3>{t(COPY.topicsHeading)}</h3>
            <div className="cq2-pills">
              {notes.topics.map(topic => (
                <span key={topic}>{topic}</span>
              ))}
            </div>
          </section>
        )}

        {submission ? (
          <div className="cq2-sent" data-testid="career-quiz2-sent">
            <Check size={18} /> {t(COPY.sent(submission.adviserLabel))}
          </div>
        ) : (
          <div className="cq-actions">
            <button type="button" className="cq-btn-secondary" onClick={back}>
              <ChevronLeft size={18} /> {t(COPY.back)}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="career-quiz-page cq2-page" data-testid="career-quiz2" lang={lang}>
      <div className="career-quiz-container">
        {props.langBar}
        {!done && disc && (
          <div className="cq-progress">
            <div
              className="cq-progress-track"
              role="progressbar"
              aria-valuenow={step + 1}
              aria-valuemin={1}
              aria-valuemax={PART_TWO_STEPS}
              aria-label={t(L('測驗進度', '测验进度', 'Quiz progress'))}
            >
              <div className="cq-progress-fill" style={{ width: `${((step + 1) / PART_TWO_STEPS) * 100}%` }} />
            </div>
            <div className="cq-progress-meta">
              <span className="cq-progress-label">
                {t(L('第二部分', '第二部分', 'Part 2'))} · {t(STEP_TITLES[step])}
              </span>
            </div>
          </div>
        )}
        <main className="cq-stage">
          {!disc ? discPicker() : done ? blueprint(disc) : <div className="cq-card">{question(disc)}</div>}
        </main>
      </div>
    </div>
  );
};

export default CareerQuizPartTwo;
